import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, Link } from "react-router-dom";
import {
  Table,
  Empty,
  Col,
  Row,
  message,
  Tooltip,
} from "antd";
import {
  RollbackOutlined,
  MenuUnfoldOutlined,
  BarsOutlined,
  CheckOutlined,
  BankOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { ColumnHelper, RowHelper } from "util/TableHelper";
import { odata, queryHelper } from "../Filters/Helper";
import RequestHandler from "../../util/RequestHandler";
import AccountPageTableFilter from "../Filters/AccountPageTableFilter";
import config from "../../util/ApiforContext";

const AccountPage = (props) => {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState({
    data: [],
    total: 0,
    loading: true,
  });
  const [tableDataQueryOptions, setTableDataQueryOptions] = useState(queryHelper.getDefaultTableDataQueryOptions());
  const history = useHistory();

  useEffect(() => {
    setFilterValues();
    getTableData();
  }, [tableDataQueryOptions]);

  const getFilterQuery = () => {
    const { filter } = tableDataQueryOptions;

    const conditions = [];

    const [bankId, currencyCode] = props.match.params.tenantid.split("-");
    if (bankId) {
      conditions.push(`bankId eq ${bankId}`);
    }

    if (currencyCode) {
      conditions.push(`currency eq '${currencyCode}'`);
    }

    if (filter) {
      conditions.push(filter);
    }

    if (conditions.length !== 0) {
      return "$filter=" + conditions.join(" and ");
    }
    return "";
  };

  const getTableData = () => {
    setTableData({
      ...tableData,
      loading: true,
    });
    const { skip, take, order } = tableDataQueryOptions;

    RequestHandler.get(
      `/TenantAccount?$count=true&${getFilterQuery()}${order}&$skip=${skip}&$top=${take}`
    ).then((response) => {
      if (Array.isArray(response.value)) {
        setTableData({
          data: response.value,
          total: response["@odata.count"],
          loading: false,
        });
      }
    });
  };

  const setFilterValues = () => {
    const filterValues = {
      ...tableDataQueryOptions.values,
      skip: tableDataQueryOptions.skip,
      take: tableDataQueryOptions.take,
      current: tableDataQueryOptions.current,
      order: tableDataQueryOptions.order,
      filter: tableDataQueryOptions.filter,
    };
    const queryString = queryHelper.getQueryFromValues(filterValues);

    history.replace({
      pathname: window.location.pathname,
      search: queryString
    });
  };

  const paging = (p, _, sorter) => {
    let orderValue = "";
    if (sorter.columnKey && sorter.order) {
      orderValue =
        "&" +
        (sorter.order === "ascend"
          ? odata.orderBy(sorter.columnKey)
          : odata.orderByDesc(sorter.columnKey));
    }

    setTableDataQueryOptions((prevState) => {
      return {
        ...prevState,
        skip: (p.current - 1) * prevState.take,
        take: p.pageSize,
        current: p.current,
        order: orderValue
      };
    });
  };

  const onFinishTableFilter = (query, values) => {
    setTableDataQueryOptions((prevState) => {
      return { ...prevState, skip: 0, current: 1, filter: query, values: values };
    });
  };

  const onClearTableFilter = () => {
    setTableDataQueryOptions({
      ...tableDataQueryOptions,
      skip: 0,
      current: 1,
      filter: "",
      values: {}
    });
  };

  const onClickExportExcel = () => {
    const hideMessage = message.loading(t("excelDownloading"), 0);

    RequestHandler.post(
      `/TenantAccount/ExportExcel`,
      {
        query: getFilterQuery(),
      },
      false
    ).then((response) => {
      hideMessage();

      if (response) {
        window.open(
          `${config.apiUrl
          }/TenantAccount/DownloadExcel?fileName=${response}&t=${RequestHandler.getToken()}`
        );
      }
    });
  };

  const columns = [
    {
      title: t("bank"),
      dataIndex: "bankLogo",
      key: "bankName",
      render: (data) => {
        return (
          <div style={{ width: 100 }}>
            {" "}
            <img src={data}></img>
          </div>
        );
      },
    },
    {
      title: t("branchNameOrCode"),
      dataIndex: "branchName",
      align: "center",
      key: "branchName",
      width: "15%",
      render: (text, row) => (
        <Tooltip placement="topLeft" title={text}>
          <div>
            {row.branchName == null
              ? row.branchCode == null
                ? ""
                : row.branchCode
              : row.branchName}
          </div>
        </Tooltip>
      ),
    },
    {
      title: t("accountName"),
      dataIndex: "name",
      align: "center",
      key: "name",
      width: "10%",
    },
    {
      title: t("ibanOrAccountNumber"),
      dataIndex: "iban",
      align: "center",
      key: "iban",
      width: "15%",
      render: (_, row) => row.iban ? row.iban : row.accountNumber,
    },
    {
      title: t("currency"),
      dataIndex: "currency",
      align: "center",
      key: "currency",
      width: "10%",
    },
    {
      title: t("amount"),
      dataIndex: "balanceString",
      key: "balance",
      sorter: true,
      align: "center",
      render: (data) => <div style={{ textAlign: "right" }}>{data}</div>,
    },
    {
      title: t("type"),
      dataIndex: "type",
      align: "center",
      key: "type",
      width: "10%",
      render: (_, row) => {
        if (row.typeValue != null) {
          return <div>{row.type}</div>;
        }
      },
    },
    {
      title: t("lastQueryDate"),
      dataIndex: "lastQueryDate",
      key: "lastQueryDate",
      sorter: true,
      align: "center",
      render: (data) => data ? (
        <div>
          {moment(data).format("L")}{" "}
          {moment(data).format("LT")}
        </div>
      ) : "-",
    },
    {
      title: t("detail"),
      dataIndex: "",
      width: "2%",
      align: "center",
      render: (record) => {
        return (
          <Link
            style={{ fontSize: "150%" }}
            to={`/bank-accounts-account-transaction/${record.id}`}
          >
            <MenuUnfoldOutlined />
          </Link>
        );
      },
    },
    {
      title: t("includeInCalculation"),
      dataIndex: "isCalculated",
      width: "2%",
      align: "center",
      render: (data) => (
        <div style={{ color: data === true ? "green" : "red" }}>
          <CheckOutlined />
        </div>
      ),
    },
  ];

  const detailColumns = [
    {
      title: t("availableBalance"),
      dataIndex: "availableBalance",
      render: ColumnHelper.renderDecimal,
    },
    {
      title: t("mainCreditLimit"),
      dataIndex: "creditLimit",
      render: ColumnHelper.renderDecimal,
    },
    {
      title: t("usedCreditBalance"),
      dataIndex: "creditBalance",
      render: ColumnHelper.renderDecimal,
    },
    {
      title: t("blockedAmount"),
      dataIndex: "blockedAmount",
      render: ColumnHelper.renderDecimal,
    },
    {
      title: t("dueDate"),
      dataIndex: "dueDate",
      render: (data) => data ? moment(data).format("DD.MM.YYYY") : "-",
    },
    {
      title: t("interestRate"),
      dataIndex: "interestRate",
    },
    {
      title: t("openDate"),
      dataIndex: "openDate",
      render: (data) => data ? moment(data).format("DD.MM.YYYY") : "-",
    },
  ];

  return (
    <>
      <Row style={{ marginBottom: 9 }}>
        <Col flex={1}>
          <a
            onClick={() => history.goBack()}
            style={{ color: "black", marginLeft: "4%" }}
          >
            {" "}
            <RollbackOutlined /> {t("goBack")}
          </a>
        </Col>
        <Col flex={4}></Col>
      </Row>

      <Row>
        <Col xs={24} md={12} lg={16}>
          <p style={{ fontSize: "120%", marginTop: -5 }}>
            <BankOutlined /> {t("bankAccounts")}
          </p>
        </Col>
        <Col xs={24} md={12} lg={8} style={{ textAlign: "right" }}>
          <Tooltip title={t("downloadExcel")}>
            <a
              style={{
                color: "green",
                fontSize: "180%",
                marginRight: 30,
              }}
              onClick={onClickExportExcel}
            >
              <FileExcelOutlined />
            </a>
          </Tooltip>
        </Col>
      </Row>

      <AccountPageTableFilter
        data={tableDataQueryOptions.values}
        onClearFilter={onClearTableFilter}
        onFinishFilter={onFinishTableFilter}
        style={{ marginTop: 12, marginBottom: 12 }}
      />

      <Row id="tableArea">
        <Col span={24}>
          <Table
            columns={columns}
            expandable={{
              expandedRowRender: (row) =>
                RowHelper.renderRowDetail({
                  data: row,
                  columns: detailColumns,
                }),
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("noData")}
                />
              ),
            }}
            style={{ overflowX: "auto", maxHeight: "100%", width: "100%" }}
            rowKey="id"
            filterable={true}
            sortable={true}
            pageable={true}
            onChange={paging}
            pagination={{
              defaultPageSize: tableDataQueryOptions.take,
              pageSizeOptions: ["15", "25", "50", "100"],
              total: tableData.total,
              current: tableDataQueryOptions.current,
            }}
            dataSource={tableData.data}
            loading={tableData.loading}
            size="small"
          />
        </Col>
      </Row>

    </>
  );
};

export default AccountPage;
